import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Router, Route, Switch, useLocation } from 'react-router-dom';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { MuiThemeProvider } from '@material-ui/core/styles';
import ReactGA from 'react-ga4';
import history from '../services/browserHistory';
import Alert from '../components/micro/Alert/Alert';
import GlobalSpinner from '../components/micro/GlobalSpinner/GlobalSpinner';
import { AppStore } from '../models/redux/store/app.store';
import { persistor } from '../services/configureStore';
import PrivateRoute from '../components/micro/PrivateRoute/PrivateRoute';
import AdminRoute from '../components/micro/PrivateRoute/AdminRoute';
import theme from './theme';
import { MODULE_ARRAY, TRAINING_NAVIGATION, BASE_ROUTE } from '../constants/trainingNav.constants';

import LandingPage from '../components/pages/LandingPage/LandingPage';
import Login from '../components/pages/Login/Login';

import AxuminTrainingHome from '../components/pages/training/TrainingHome/AxuminTrainingHome';
import PoslumaTrainingHome from '../components/pages/training/TrainingHome/PoslumaTrainingHome';
import TrainingPage from '../components/pages/training/TrainingPage/TrainingPage';
import TrainingListHome from '../components/pages/training/TrainingListHome/TrainingListHome';
import AxuminTrainingOverview from '../components/pages/training/TrainingOverview/AxuminTrainingOverview';
import PoslumaTrainingOverview from '../components/pages/training/TrainingOverview/PoslumaTrainingOverview';
import Registration from '../components/pages/Registration/Registration';
import AdminPortal from '../components/pages/AdminPortal/AdminPortal';
import ProfilePage from '../components/pages/ProfilePage/ProfilePage';
import AxuminTrainingEnd from '../components/pages/training/TrainingEnd/AxuminTrainingEnd';
import PoslumaTrainingEnd from '../components/pages/training/TrainingEnd/PoslumaTrainingEnd';
import PasswordReset from '../components/pages/PasswordReset/PasswordReset';
import PasswordSet from '../components/pages/PasswordSet/PasswordSet';
import TermsAndConditions from '../components/pages/TermsAndConditions/TermsAndConditions';
import LearningCheckReview from '../components/pages/training/LearningCheckReview/LearningCheckReview';
import AxuminDownloadableResources from '../components/pages/DownloadableResources/AxuminDownloadableResources';
import AxuminAdditionalCaseStudies from '../components/pages/training/AdditionalCaseStudies/AxuminAdditionalCaseStudies';
import PoslumaDownloadableResources from '../components/pages/DownloadableResources/PoslumaDownloadableResources';
import PoslumaAdditionalCaseStudies from '../components/pages/training/AdditionalCaseStudies/PoslumaAdditionalCaseStudies';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const GoogleAnalytics = () => {
  useEffect(() => {
    ReactGA.initialize('G-PLGYHPSM6Z');
  }, []);
  const { pathname } = useLocation();
  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: pathname,
    });
  }, [pathname]);
  return null;
};

type AppProps = {
  store: AppStore;
};

const App = ({ store }: AppProps): JSX.Element => {
  const modules: { name: string; id: number }[] = [];
  MODULE_ARRAY.forEach((training) => {
    training.modules.forEach((module) => {
      modules.push({
        name: module,
        id: training.trainingId,
      });
    });
  });

  const trainingRoutesArray = modules.map((module) => {
    const navigationEntries = Object.entries(TRAINING_NAVIGATION[module.name]);
    const navigationRoutes = navigationEntries.map((entry) => {
      const [key, value] = entry;
      return (
        <Route
          exact
          key={key}
          path={`${BASE_ROUTE[module.name]}${value.route}`}
          render={() => <TrainingPage trainingId={module.id} module={module.name} page={Number(key)} />}
        />
      );
    });
    return { ...module, navigationRoutes };
  });

  return (
    <MuiThemeProvider theme={theme}>
      <Provider store={store}>
        <PersistGate persistor={persistor}>
          <GlobalSpinner />
          <Alert />
          <BrowserRouter>
            <Router history={history}>
              <GoogleAnalytics />
              <ScrollToTop />
              <Switch>
                <Route path="/login">
                  <Login />
                </Route>
                <Route path="/user-registration">
                  <Registration />
                </Route>
                <Route path="/reset">
                  <PasswordReset />
                </Route>
                <Route path="/set-password">
                  <PasswordSet />
                </Route>
                <Route path="/termsconditions">
                  <TermsAndConditions />
                </Route>

                <AdminRoute path="/admin" component={AdminPortal} />
                <PrivateRoute path="/profile" component={ProfilePage} />

                {/* Dynamic routes for all training for all modules */}
                {/* TODO Have to customize the Private Route for this so that it pushes you back
                if you are not ready for that step */}
                {trainingRoutesArray.map((module) => {
                  return module.navigationRoutes.map((route) => route);
                })}

                {/* Axumin routes */}
                <PrivateRoute path="/axumin/casestudies" component={AxuminAdditionalCaseStudies} />
                <PrivateRoute path="/axumin/downloadableresources" component={AxuminDownloadableResources} />
                <PrivateRoute path="/axumin/acquisition/learningcheckreview" component={LearningCheckReview} />
                <PrivateRoute path="/axumin/acquisition/thankyou" component={AxuminTrainingEnd} />
                <PrivateRoute path="/axumin/acquisition" component={AxuminTrainingOverview} />
                <PrivateRoute path="/axumin/interpretation/learningcheckreview" component={LearningCheckReview} />
                <PrivateRoute path="/axumin/interpretation/thankyou" component={AxuminTrainingEnd} />
                <PrivateRoute path="/axumin/interpretation" component={AxuminTrainingOverview} />
                <PrivateRoute path="/axumin" component={AxuminTrainingHome} />

                {/* POSLUMA routes */}
                <PrivateRoute path="/posluma/casestudies" component={PoslumaAdditionalCaseStudies} />
                <PrivateRoute path="/posluma/downloadableresources" component={PoslumaDownloadableResources} />
                <PrivateRoute path="/posluma/acquisition/thankyou" component={PoslumaTrainingEnd} />
                <PrivateRoute path="/posluma/acquisition" component={PoslumaTrainingOverview} />
                <PrivateRoute path="/posluma/interpretation/thankyou" component={PoslumaTrainingEnd} />
                <PrivateRoute path="/posluma/interpretation" component={PoslumaTrainingOverview} />
                <PrivateRoute path="/trainings" component={TrainingListHome} />
                <PrivateRoute path="/posluma" component={PoslumaTrainingHome} />

                <Route path="/">
                  <LandingPage />
                </Route>
              </Switch>
            </Router>
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </MuiThemeProvider>
  );
};

export default App;
